import {
  Box,
  Button,
  Checkbox,
  HStack,
  Icon,
  IconButton,
  Link,
  NativeSelect,
  RadioGroup,
  Separator,
  SimpleGrid,
  Spacer,
  Stack,
  Tag,
  Text,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useStores } from "../../../shared/stores/RootStore";
import { ArrowBackIcon, SaveIcon } from "../../../shared/views/icons";
import { t } from "i18next";
import { toast } from "../../../shared/views/CustomToaster";
import { hlaAttributeOptions } from "../../../map/models/HlaAttribute";
import { FieldWrapper } from "../HlaWayDetail/form/FieldWrapper";
import { HeavyVehicleFlowType } from "@keita/lanepatrol-sdk";
import { RadioOptions } from "../HlaWayDetail/form/RadioOptions";
import { NumberField } from "../HlaWayDetail/form/NumberField";
import { CrossSectionView } from "../HlaWayDetail/cross-section/CrossSectionView";
import { NewLaneButton, copyLane } from "../HlaWayDetail/NewLaneButton";
import { HlaWayType } from "../../models/HlaWay";
import { buildCreateOrUpdateHlaLane } from "../../models/HlaLane";
import { HlaLaneForm } from "../HlaWayDetail/form/HlaLaneForm";
import { WayDetailDelete } from "../HlaWayDetail/WayDetailDelete";
import { useEffect } from "react";
import { MapMode } from "../../../survey/stores/MapStore";

const SimulationWayDetailBase = () => {
  const { mapStore, surveyStore, hlaForecastStore, simulationStore } =
    useStores();

  const simulationWay = simulationStore.selectedSimulationWay;
  const selectedLane = simulationStore.getSelectedFormLane();

  const survey = surveyStore.getSelected();
  const noPermissions = !survey?.permissions?.canCodeHla;
  const canEdit = !noPermissions && !hlaForecastStore.isForecastModeActivated();
  const form = simulationStore.form;

  useEffect(() => {
    mapStore.setMode(MapMode.SimulationCoding);

    return () => {
      mapStore.setMode(MapMode.HlaCoding);
    };
    // eslint-disable-next-line
  }, []);

  const saveChanges = async ({ verify = false } = {}) => {
    if (verify) simulationStore.verify();

    try {
      await simulationStore.saveChanges({ finish: verify });
      toast("success", "Saved");
    } catch {
      toast("error", "Failed to save");
    }
  };

  return (
    <Stack py={2} overflow="auto" w="full">
      <HStack px={6}>
        <Box flexShrink={0} width="40px">
          <IconButton
            size="sm"
            variant="ghost"
            onClick={() => simulationStore.backToHla()}
          >
            <Icon as={ArrowBackIcon} />
          </IconButton>
        </Box>

        <Text px={2} fontWeight="semibold">
          {simulationWay?.name ?? "Unknown street"}
        </Text>

        <Tag.Root colorPalette={"primary"}>
          <Tag.Label>Simulation</Tag.Label>
        </Tag.Root>

        {canEdit && simulationWay && (
          <WayDetailDelete
            onDelete={() => simulationStore.delete(simulationWay.id)}
          />
        )}

        <Spacer />

        {canEdit && (
          <HStack gap={2}>
            <Button
              colorPalette="primary"
              variant={"ghost"}
              size="sm"
              fontSize="sm"
              onClick={() => saveChanges()}
            >
              <SaveIcon /> Save
            </Button>

            <Button
              colorPalette="primary"
              size="sm"
              fontSize="sm"
              onClick={() => saveChanges({ verify: true })}
              loading={simulationStore.savingChanges}
            >
              <SaveIcon /> Finish simulation
            </Button>
          </HStack>
        )}
      </HStack>

      <Stack gap={6}>
        <Stack px={6}>
          <Text textStyle="lg">Road</Text>

          <SimpleGrid columns={{ xl: 1, "2xl": 2 }} gap={6}>
            <FieldWrapper label={t("shared:hlaAttribute.type")}>
              <NativeSelect.Root
                size="sm"
                fontSize="sm"
                disabled={!canEdit}
                invalid={form.type == null}
              >
                <NativeSelect.Field
                  value={form.type}
                  onChange={(e) => {
                    simulationStore.code((prev) => ({
                      ...prev,
                      type: e.target.value as HlaWayType,
                    }));
                  }}
                >
                  {hlaAttributeOptions.type!.map((option) => (
                    <option value={option.value} key={option.value}>
                      {t(option.translation)}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </FieldWrapper>

            <FieldWrapper label={t("shared:hlaAttribute.heavyVehicleFlow")}>
              <RadioGroup.Root
                colorPalette="primary"
                disabled={!canEdit}
                value={form.heavyVehicleFlow}
                onValueChange={({ value }) => {
                  simulationStore.code((prev) => ({
                    ...prev,
                    heavyVehicleFlow: value as HeavyVehicleFlowType,
                  }));
                }}
              >
                <RadioOptions options={hlaAttributeOptions.heavyVehicleFlow!} />
              </RadioGroup.Root>
            </FieldWrapper>

            <FieldWrapper
              label={t("shared:hlaAttribute.averageRoadSpeed", {
                speedUnit: surveyStore.speedUnitStr,
              })}
            >
              <NumberField
                step={10}
                disabled={!canEdit}
                value={form.averageRoadSpeed}
                onChange={(value) => {
                  simulationStore.code((prev) => ({
                    ...prev,
                    averageRoadSpeed: value,
                  }));
                }}
              />

              {form.averageRoadSpeed == null && (
                <Link
                  fontSize="xs"
                  onClick={() => surveyStore.setShowSurveyDefaultsModal(true)}
                >
                  {`Value defaults to ${surveyStore.getDefaultAverageRoadSpeed(
                    form.type,
                  )} for road type "${t(`shared:hlaWayType.${form.type}`)}"`}
                </Link>
              )}
            </FieldWrapper>

            <FieldWrapper label={t("shared:hlaAttribute.averageDailyTraffic")}>
              <NumberField
                step={1000}
                disabled={!canEdit}
                value={form.averageDailyTraffic}
                onChange={(value) => {
                  simulationStore.code((prev) => ({
                    ...prev,
                    averageDailyTraffic: value,
                  }));
                }}
              />

              {form.averageDailyTraffic == null && (
                <Link
                  fontSize="xs"
                  onClick={() => surveyStore.setShowSurveyDefaultsModal(true)}
                >
                  {`Value defaults to ${surveyStore.getDefaultAverageDailyTraffic(
                    form.type,
                  )} for road type "${t(`shared:hlaWayType.${form.type}`)}"`}
                </Link>
              )}
            </FieldWrapper>

            <FieldWrapper label={t("shared:hlaAttribute.lit")}>
              <Checkbox.Root
                disabled={!canEdit}
                colorPalette="primary"
                checked={form.lit}
                onCheckedChange={({ checked }) => {
                  simulationStore.code((prev) => ({
                    ...prev,
                    lit: checked as boolean,
                  }));
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
              </Checkbox.Root>
            </FieldWrapper>
          </SimpleGrid>
        </Stack>

        <Stack>
          <Separator />
          <Text textStyle="lg" px={6}>
            Bike lanes
          </Text>
        </Stack>

        <HStack
          justifyContent="center"
          flexDir={surveyStore.rightHandTraffic ? "row" : "row-reverse"}
        >
          {form.backFacingLane ? (
            <CrossSectionView
              lit={form.lit}
              lane={form.backFacingLane}
              canEdit={canEdit}
              isSelected={!simulationStore.defaultToFrontFacing}
              score={simulationStore.getSelectedLane()?.score}
              onSelect={() => simulationStore.selectSimulationLane(false)}
              onDelete={() => {
                simulationStore.code((prev) => {
                  prev.backFacingLane = null;
                  return { ...prev };
                });
                simulationStore.selectSimulationLane(true);
              }}
            />
          ) : (
            <NewLaneButton
              frontFacing={false}
              canEdit={canEdit}
              onAdd={(frontFacing, directionKey, otherKey) => {
                simulationStore.code((prev: any) => {
                  const otherLane = prev[otherKey];

                  return {
                    ...prev,
                    [directionKey]: otherLane
                      ? copyLane(otherLane)
                      : buildCreateOrUpdateHlaLane(frontFacing),
                  };
                });

                simulationStore.selectSimulationLane(false);
              }}
            />
          )}

          {form.frontFacingLane ? (
            <CrossSectionView
              lit={form.lit}
              lane={form.frontFacingLane}
              canEdit={canEdit}
              isSelected={simulationStore.defaultToFrontFacing}
              score={simulationStore.getSelectedLane()?.score}
              onSelect={() => simulationStore.selectSimulationLane(true)}
              onDelete={() => {
                simulationStore.code((prev) => {
                  prev.frontFacingLane = null;
                  return { ...prev };
                });
                simulationStore.selectSimulationLane(false);
              }}
            />
          ) : (
            <NewLaneButton
              frontFacing={true}
              canEdit={canEdit}
              onAdd={(frontFacing, directionKey, otherKey) => {
                simulationStore.code((prev: any) => {
                  const otherLane = prev[otherKey];

                  return {
                    ...prev,
                    [directionKey]: otherLane
                      ? copyLane(otherLane)
                      : buildCreateOrUpdateHlaLane(frontFacing),
                  };
                });

                simulationStore.selectSimulationLane(true);
              }}
            />
          )}
        </HStack>

        <Separator />

        {!!selectedLane && (
          <HlaLaneForm
            lane={selectedLane}
            code={simulationStore.code as any}
            way={simulationStore.selectedSimulationWay}
          />
        )}
      </Stack>
    </Stack>
  );
};

export const SimulationWayDetail = observer(SimulationWayDetailBase);
